"use client";

import React, { createContext, useState, useContext, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from './AuthContext';
import tenantManager from '../lib/tenant/tenant-manager';
import { authAPI } from '../lib/api';
import tenantAPI from '../lib/tenant/tenant-api';
import tenantService from '../lib/tenant/tenant-service';
import { Tenant } from '../lib/tenant/types';
import { mockTenants } from '../lib/mockApi'; // Fallback pour le développement
import tenantBrandingService from '../lib/tenant/tenant-branding';
import UserDataManager from '../lib/auth/user-data-manager';
import useTokenManager from '../hooks/useTokenManager';
import { toast } from 'react-hot-toast';

// Type pour le contexte de tenant
type TenantContextType = {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  switchTenant: (tenantId: string) => Promise<boolean>;
  getTenantById: (tenantId: string) => Tenant | undefined;
  reloadTenants: () => Promise<void>;
};

// Création du contexte
const TenantContext = createContext<TenantContextType>({
  currentTenant: null,
  tenants: [],
  isLoading: false,
  switchTenant: async () => false,
  getTenantById: () => undefined,
  reloadTenants: async () => {},
});

// Hook personnalisé pour utiliser le contexte
export const useTenant = () => useContext(TenantContext);

// Fournisseur du contexte
export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();
  const tokenManager = useTokenManager();

  // Fonction pour forcer le rechargement des tenants
  const reloadTenants = async (): Promise<void> => {
    console.log('🔄🔄 TenantContext: Début de reloadTenants', new Date().toLocaleString());
    console.log('🔄 TenantContext: Rechargement des tenants...');
    setIsLoading(true);
    
    try {
      // Vérifier si nous sommes en mode développement avec bypass
      const isDevBypass = process.env.NEXT_PUBLIC_DEV_BYPASS === 'true' || process.env.NEXT_PUBLIC_STANDALONE_MODE === 'true';
      
      if (isDevBypass) {
        console.log('🛠️ TenantContext: Mode développement avec bypass, pas besoin de s\'authentifier');
      } 
      // Vérifier l'authentification seulement si nous ne sommes pas en mode bypass
      else if (!tokenManager.isAuthenticated()) {
        console.log('🔐 TenantContext: Tentative de rafraîchissement du token');
        
        // Essayer de rafraîchir le token
        const refreshed = await authAPI.refreshToken().catch(() => false);
        
        if (!refreshed) {
          console.error('❌ TenantContext: Non authentifié et impossible de rafraîchir le token');
          setTenants([]);
          setCurrentTenant(null);
          setIsLoading(false);
          return;
        }
      }
      
      // Synchroniser le cookie user-data
      UserDataManager.synchronizeUserData();
      
      // Synchroniser les tenants via le TenantService
      console.log('🏢 TenantContext: Synchronisation via TenantService');
      await tenantService.synchronizeTenants();
      
      // Récupérer les tenants
      let fetchedTenants: Tenant[] = [];
      
      try {
        // Utiliser le TenantService pour récupérer les tenants
        fetchedTenants = await tenantService.getAllTenants();
        console.log('✅ TenantContext: Tenants récupérés avec succès:', fetchedTenants.length);
      } catch (serviceError) {
        console.error('❌ TenantContext: Erreur TenantService:', serviceError);
        
        // Fallback direct vers l'API
        try {
          console.log('⚠️ TenantContext: Tentative via API directe');
          fetchedTenants = await tenantAPI.getUserTenants();
          console.log('✅ TenantContext: Tenants récupérés via API:', fetchedTenants.length);
        } catch (apiError) {
          console.error('❌ TenantContext: Échec API:', apiError);
          
          // Dernier recours: localStorage
          try {
            const storedTenantsJson = localStorage.getItem('availableTenants');
            if (storedTenantsJson) {
              const storedTenants = JSON.parse(storedTenantsJson);
              if (Array.isArray(storedTenants) && storedTenants.length > 0) {
                fetchedTenants = storedTenants;
                console.log('💾 TenantContext: Utilisation localStorage:', fetchedTenants.length);
              }
            }
          } catch (storageError) {
            console.error('❌ TenantContext: Erreur localStorage:', storageError);
          }
        }
      }
      
      // Mock tenants en développement si nécessaire
      if (fetchedTenants.length === 0 && process.env.NODE_ENV !== 'production') {
        console.log('💡 TenantContext: Utilisation des mock tenants');
        fetchedTenants = mockTenants;
      }
      
      // Filtrer les tenants invalides
      const validTenants = fetchedTenants.filter((tenant: Tenant) => {
        if (!tenant || !tenant.id) {
          console.warn('⚠️ Tenant sans ID filtré:', tenant);
          return false;
        }
        
        // Filtrer les tenants de test en production
        const isTestTenant = (typeof tenant.id === 'string' && tenant.id.includes('test')) || 
                          (tenant.name && typeof tenant.name === 'string' && tenant.name.toLowerCase().includes('test')) || 
                          (typeof tenant.id === 'string' && tenant.id.endsWith('001')) || 
                          (typeof tenant.id === 'string' && tenant.id.endsWith('002')) || 
                          (typeof tenant.id === 'string' && tenant.id.endsWith('003'));
        
        const isProduction = process.env.NODE_ENV === 'production';
        
        if (isTestTenant && isProduction) {
          console.log('💬 Tenant de test filtré en production:', tenant.id);
          return false;
        }
        
        return true;
      });
      
      // Mettre à jour les tenants filtrés
      const filteredTenants = validTenants;
      
      // Initialiser le TenantManager si nécessaire
      if (filteredTenants.length > 0) {
        // Préparer les données pour l'initialisation
        const storedTenantId = localStorage.getItem('currentTenantId');
        const currentTenantId = storedTenantId || filteredTenants[0].id;
        
        const tenantUserData = {
          tenantId: currentTenantId,
          tenantRole: 'owner',
          tenants: filteredTenants.map((t: Tenant) => ({ tenantId: t.id, role: 'owner' }))
        };
        
        // Initialiser le TenantManager
        tenantManager.initialize(tenantUserData, filteredTenants);
        
        // Mettre à jour le localStorage
        try {
          localStorage.setItem('availableTenants', JSON.stringify(filteredTenants));
          localStorage.setItem('lastTenantReloadTimestamp', Date.now().toString());
        } catch (storageError) {
          console.warn('⚠️ Erreur localStorage:', storageError);
        }
      }
      
      // Mettre à jour l'état avec les tenants filtrés
      setTenants(filteredTenants);
      
      // Déterminer le tenant courant
      if (filteredTenants.length > 0) {
        const storedTenantId = localStorage.getItem('currentTenantId');
        let newCurrentTenant: Tenant | null = null;
        
        if (storedTenantId) {
          newCurrentTenant = filteredTenants.find((t: Tenant) => t.id === storedTenantId) || null;
        }
        
        if (!newCurrentTenant && filteredTenants.length > 0) {
          newCurrentTenant = filteredTenants[0] as Tenant;
        }
        
        setCurrentTenant(newCurrentTenant);
        
        // Appliquer le branding du tenant sélectionné
        if (newCurrentTenant && typeof newCurrentTenant.id === 'string') {
          tenantBrandingService.applyBranding(newCurrentTenant.id);
        }
      } else {
        setCurrentTenant(null);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('❌ TenantContext: Erreur générale:', error);
      setIsLoading(false);
      
      // Restaurer depuis le localStorage en cas d'erreur
      try {
        const storedTenantsJson = localStorage.getItem('availableTenants');
        if (storedTenantsJson) {
          const storedTenants = JSON.parse(storedTenantsJson);
          if (Array.isArray(storedTenants) && storedTenants.length > 0) {
            setTenants(storedTenants);
            
            const currentTenantId = localStorage.getItem('currentTenantId');
            const storedCurrentTenant = storedTenants.find((t: Tenant) => 
              t.id === currentTenantId
            ) || storedTenants[0];
            
            setCurrentTenant(storedCurrentTenant);
          }
        }
      } catch (storageError) {
        console.error('❌ Erreur lors de la récupération de secours:', storageError);
      }
    }
  };

  useEffect(() => {
    // Empêcher tout chargement tant que le contexte d'auth n'est pas prêt
    if (authLoading) {
      setIsLoading(true);
      console.log('⏳ TenantContext: Attente de la fin du chargement du contexte d\'authentification...');
      return;
    }
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      setTenants([]);
      setCurrentTenant(null);
      console.log('🔒 TenantContext: Utilisateur non authentifié ou profil absent. Aucun tenant chargé.');
      return;
    }
    reloadTenants();
  }, [isAuthenticated, authLoading]);

  // Ajouter un écouteur d'événement pour détecter les changements de stockage
  // Cela permet de synchroniser les tenants entre les différentes pages
  const handleStorageChange = (e: StorageEvent): void => {
    if (e.key === 'currentTenantId' && e.newValue) {
      const newTenantId = e.newValue;
      const tenant = tenants.find(t => t.id === newTenantId);
      if (tenant && (!currentTenant || currentTenant.id !== newTenantId)) {
        setCurrentTenant(tenant);
        tenantBrandingService.applyBranding(newTenantId);
      }
    }
  };

  // Effet pour charger les données initiales


  // Effet pour écouter les changements de localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
    
    return () => {};
  }, [tenants, currentTenant]);

  // Effet pour vérifier si le tenant actuel est toujours valide
  useEffect(() => {
    if (tenants.length > 0 && !currentTenant) {
      const lastSelectedTenantId = localStorage.getItem('currentTenantId');
      
      // Si un tenant a été sélectionné précédemment
      if (lastSelectedTenantId) {
        const lastSelectedTenant = tenants.find(t => t.id === lastSelectedTenantId);
        if (lastSelectedTenant) {
          setCurrentTenant(lastSelectedTenant);
          tenantBrandingService.applyBranding(lastSelectedTenant.id);
          return;
        }
      }
      
      // Si aucun tenant n'a été trouvé ou sélectionné, utiliser le premier
      setCurrentTenant(tenants[0]);
      tenantBrandingService.applyBranding(tenants[0].id);
    }
  }, [tenants, currentTenant]);

  // Effet pour retenter le chargement si aucun tenant n'a été trouvé
  useEffect(() => {
    if (!isLoading && tenants.length === 0 && isAuthenticated && !authLoading) {
      let attemptCount = 0;
      const maxAttempts = 3;
      
      const baseDelay = 5000; // Délai de base de 5 secondes
      
      // Stocker la dernière tentative de rechargement pour éviter les appels trop fréquents
      const lastReloadTimestamp = parseInt(localStorage.getItem('lastTenantReloadTimestamp') || '0');
      const currentTime = Date.now();
      const minTimeBetweenReloads = 30000; // 30 secondes minimum entre les rechargements
      
      // Vérifier si un rechargement a été effectué récemment
      const recentlyReloaded = (currentTime - lastReloadTimestamp) < minTimeBetweenReloads;
      
      if (!recentlyReloaded) {
        console.log('TenantContext: Aucun tenant trouvé, nouvelle tentative programmée...');
        
        const timer = setTimeout(() => {
          console.log(`TenantContext: Tentative de rechargement ${attemptCount + 1}/${maxAttempts}...`);
          reloadTenants();
          localStorage.setItem('lastTenantReloadTimestamp', Date.now().toString());
        }, baseDelay);
        
        return () => clearTimeout(timer);
      }
    }
    
    return () => {};
  }, [isLoading, tenants, isAuthenticated, authLoading]);

  // Fonction pour changer de tenant avec gestion d'erreur améliorée
  const switchTenant = async (tenantId: string): Promise<boolean> => {
    console.log(`🔄 TenantContext - Tentative de changement vers le tenant ${tenantId}`);
    
    try {
      // Vérifier si l'utilisateur est authentifié
      if (!tokenManager.isAuthenticated()) {
        console.error(`❌ TenantContext - Utilisateur non authentifié, impossible de changer de tenant`);
        return false;
      }
      
      // Empêcher le changement si c'est le même tenant
      if (currentTenant?.id === tenantId) {
        console.log(`🔄 TenantContext - Déjà sur le tenant ${tenantId}, aucun changement nécessaire`);
        return true; // Retourner true car le tenant est déjà sélectionné
      }
      
      // Vérifier si le tenant existe dans la liste
      const tenantExists = Array.isArray(tenants) && tenants.some(t => t.id === tenantId);
      if (!tenantExists) {
        console.warn(`⚠️ TenantContext - Le tenant ${tenantId} n'existe pas dans la liste des tenants disponibles`);
        reloadTenants().then(() => {
          // Vérifier si le tenant existe après rechargement
          const reloadedTenantExists = tenants.some(t => t.id === tenantId);
          if (reloadedTenantExists) {
            console.log(`🔄 TenantContext - Tenant ${tenantId} trouvé après rechargement, nouvelle tentative`);
            switchTenant(tenantId);
          }
        });
        return false;
      }
      
      // Enregistrer la tentative de changement pour diagnostic
      try {
        if (typeof localStorage !== 'undefined') {
          const switchAttempts = JSON.parse(localStorage.getItem('sentinel_tenant_switch_attempts') || '[]');
          switchAttempts.push({
            tenantId,
            timestamp: Date.now(),
            userId: sessionStorage.getItem('userId') || 'unknown',
            currentTenantId: tenantManager.getCurrentTenantId() || 'none'
          });
          // Limiter à 20 tentatives maximum
          if (switchAttempts.length > 20) switchAttempts.splice(0, switchAttempts.length - 20);
          localStorage.setItem('sentinel_tenant_switch_attempts', JSON.stringify(switchAttempts));
        }
      } catch (e) {
        // Ignorer les erreurs de localStorage
      }
      
      // Vérifier si le tenant est accessible
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.error(`❌ TenantContext - L'utilisateur n'a pas accès au tenant: ${tenantId}`);
        
        // Vérifier si le tenant existe dans la liste des tenants
        const tenantExists = Array.isArray(tenants) && tenants.some(t => t.id === tenantId);
        const tenantName = tenantExists ? tenants.find(t => t.id === tenantId)?.name || tenantId : tenantId;
        
        // Enregistrer l'erreur d'accès pour diagnostic
        try {
          if (typeof localStorage !== 'undefined') {
            const accessErrors = JSON.parse(localStorage.getItem('sentinel_tenant_access_errors_context') || '[]');
            accessErrors.push({
              tenantId,
              tenantName,
              timestamp: Date.now(),
              userId: sessionStorage.getItem('userId') || 'unknown',
              exists: tenantExists,
              currentTenantId: tenantManager.getCurrentTenantId() || 'none'
            });
            // Limiter à 10 erreurs maximum
            if (accessErrors.length > 10) accessErrors.shift();
            localStorage.setItem('sentinel_tenant_access_errors_context', JSON.stringify(accessErrors));
          }
        } catch (e) {
          // Ignorer les erreurs de localStorage
        }
        
        if (tenantExists) {
          console.warn(`⚠️ TenantContext - Le tenant ${tenantName} (${tenantId}) existe dans la liste des tenants mais n'est pas accessible`);
          toast.error(
            <div>
              <p><strong>Accès refusé à "{tenantName}"</strong></p>
              <p className="text-xs mt-1">Contactez l'administrateur pour obtenir les droits d'accès.</p>
            </div>,
            { duration: 5000 }
          );
          
          // Tenter de recharger les tenants pour mettre à jour les permissions
          if (typeof reloadTenants === 'function') {
            console.log(`🔄 TenantContext - Tentative de rechargement des tenants pour mettre à jour les permissions`);
            setTimeout(() => {
              reloadTenants().then(() => {
                // Après le rechargement, vérifier si l'accès est maintenant possible
                console.log(`✅ TenantContext - Rechargement des tenants terminé, vérification d'accès`);
                // Vérifier si l'accès a été accordé après le rechargement
                if (tenantManager.isTenantAccessible(tenantId)) {
                  console.log(`🔓 TenantContext - Accès accordé après rechargement pour le tenant ${tenantName}`);
                  switchTenant(tenantId); // Réessayer le changement de tenant
                } else {
                  console.warn(`⚠️ TenantContext - Accès toujours refusé après rechargement pour ${tenantName}`);
                  // Afficher un message indiquant que l'accès est toujours refusé
                  toast.error(
                    <div>
                      <p><strong>Accès toujours refusé</strong></p>
                      <p className="text-xs mt-1">Malgré la mise à jour des données, vous n'avez pas accès à "{tenantName}".</p>
                    </div>,
                    { duration: 5000 }
                  );
                }
              }).catch(error => {
                console.error(`❌ TenantContext - Erreur lors du rechargement des tenants:`, error);
                toast.error(`Erreur lors de la mise à jour des données: ${error.message || 'Erreur inconnue'}`);
              });
            }, 500);
          }
        } else {
          console.warn(`⚠️ TenantContext - Le tenant ${tenantId} n'existe pas dans la liste des tenants disponibles`); 
          toast.error(`L'organisation demandée n'existe pas ou a été supprimée.`);
          
          // Recharger les tenants pour vérifier si le tenant existe réellement
          if (typeof reloadTenants === 'function') {
            setTimeout(() => reloadTenants(), 500);
          }
        }
        
        return false;
      }

      try {
        // Enregistrer l'horodatage avant le changement
        const switchStartTime = Date.now();
        
        // Changer le tenant dans le gestionnaire
        console.log(`🔄 TenantContext - Changement du tenant dans le gestionnaire: ${tenantId}`);
        const success = tenantManager.setCurrentTenant(tenantId);
        
        if (!success) {
          console.error(`❌ TenantContext - Échec lors du changement de tenant dans le gestionnaire: ${tenantId}`);
          
          // Enregistrer l'échec pour diagnostic
          try {
            if (typeof localStorage !== 'undefined') {
              const switchFailures = JSON.parse(localStorage.getItem('sentinel_tenant_switch_failures') || '[]');
              switchFailures.push({
                tenantId,
                timestamp: Date.now(),
                userId: sessionStorage.getItem('userId') || 'unknown',
                reason: 'manager_rejection',
                duration: Date.now() - switchStartTime
              });
              if (switchFailures.length > 10) switchFailures.shift();
              localStorage.setItem('sentinel_tenant_switch_failures', JSON.stringify(switchFailures));
            }
          } catch (e) {
            // Ignorer les erreurs de localStorage
          }
          
          toast.error(
            <div>
              <p><strong>Changement d'organisation impossible</strong></p>
              <p className="text-xs mt-1">Une erreur interne s'est produite. Veuillez réessayer ou actualiser la page.</p>
            </div>,
            { duration: 5000 }
          );
          return false;
        }

        // Mettre à jour l'état
        const tenant = tenants.find(t => t.id === tenantId) || null;
        if (!tenant) {
          console.warn(`⚠️ TenantContext - Tenant non trouvé dans la liste des tenants après validation d'accès: ${tenantId}`);
          
          // Tenter de recharger les tenants pour trouver le tenant manquant
          if (typeof reloadTenants === 'function') {
            setTimeout(() => reloadTenants(), 500);
          }
        }
        
        console.log(`🔄 TenantContext - Mise à jour de l'état avec le nouveau tenant:`, 
          tenant ? { id: tenant.id, name: tenant.name } : 'null');
        setCurrentTenant(tenant);
        
        // Enregistrer le succès pour diagnostic
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('sentinel_last_successful_tenant_context_switch', JSON.stringify({
              tenantId,
              tenantName: tenant?.name || 'unknown',
              timestamp: Date.now(),
              duration: Date.now() - switchStartTime,
              previousTenantId: sessionStorage.getItem('lastTenantId') || 'unknown'
            }));
            // Sauvegarder l'ID du tenant précédent pour référence
            sessionStorage.setItem('lastTenantId', tenantId);
          }
        } catch (e) {
          // Ignorer les erreurs de localStorage
        }

        // Appliquer la personnalisation du tenant
        if (tenant) {
          console.log(`🎨 TenantContext - Application de la personnalisation pour le tenant: ${tenantId}`);
          tenantBrandingService.applyBranding(tenantId);
        }

        // Stocker le tenant actif dans le localStorage et déclencher un événement global
        try {
          localStorage.setItem('currentTenantId', tenantId);
          console.log(`💾 TenantContext - Tenant sauvegardé dans le localStorage: ${tenantId}`);
          
          // Déclencher un événement pour informer tous les composants du changement
          try {
            const switchEvent = new CustomEvent('tenantSwitched', { 
              detail: { 
                tenantId, 
                source: 'tenantContext',
                timestamp: Date.now(),
                tenantName: tenant?.name || ''
              } 
            });
            
            // Vérifier que le document est disponible (peut être null lors de l'initialisation SSR)
            if (typeof document !== 'undefined' && document) {
              document.dispatchEvent(switchEvent);
              
              // Déclencher un second événement après un court délai
              setTimeout(() => {
                try {
                  if (typeof document !== 'undefined' && document) {
                    const confirmEvent = new CustomEvent('tenantConfirmed', { 
                      detail: { 
                        tenantId, 
                        source: 'tenantContext',
                        timestamp: Date.now() 
                      } 
                    });
                    document.dispatchEvent(confirmEvent);
                  }
                } catch (innerError) {
                  console.warn('⚠️ TenantContext - Erreur lors de l\'envoi de l\'événement confirmé:', innerError);
                }
              }, 300);
            }
          } catch (eventError) {
            console.warn('⚠️ TenantContext - Erreur lors de la création/envoi de l\'événement:', eventError);
          }
        } catch (e) {
          console.warn('⚠️ TenantContext - Erreur lors de la sauvegarde du tenant actif:', e);
        }

        console.log(`✅ TenantContext - Changement de tenant réussi: ${tenantId}`);
        return true;
      } catch (error) {
        console.error(`❌ TenantContext - Erreur inattendue lors du changement de tenant:`, error);
        return false;
      }
    } catch (outerError) {
      console.error(`❌ TenantContext - Erreur lors du chargement de TokenManager:`, outerError);
      return false;
    }
  };

  // Sélection automatique du premier tenant si aucun n'est sélectionné
  useEffect(() => {
    if (
      tenants.length > 0 &&
      (!currentTenant || !tenants.some(t => t.id === currentTenant?.id))
    ) {
      console.log('[TenantContext] Sélection automatique du premier tenant:', tenants[0]);
      switchTenant(tenants[0].id);
    }
  }, [tenants, currentTenant]);
  
  // Effet pour s'assurer que le tenant persiste lors des changements de page
  useEffect(() => {
    // Fonction pour restaurer le tenant actif depuis localStorage
    const restoreTenantFromStorage = async (): Promise<void> => {
      try {
        // Obtenir l'ID du tenant stocké et celui du tenant actif
        const storedTenantId = localStorage.getItem('currentTenantId');
        const contextTenantId = currentTenant?.id || null;
        const currentTenantId = tenantManager.getCurrentTenantId();
        
        // Afficher les infos de débogage
        console.log(`🔍 TenantContext - Navigation: vérification de synchronisation`);
        
        console.log(`🔍 TenantContext - Navigation: vérification de persistance du tenant:`);
        console.log(`- Tenant stocké dans localStorage: ${storedTenantId || 'aucun'}`);
        console.log(`- Tenant actif dans TenantManager: ${currentTenantId || 'aucun'}`);
        console.log(`- Tenant actif dans le contexte: ${contextTenantId || 'aucun'}`);
        
        // Vérifier si les trois sources sont en accord
        if (storedTenantId && currentTenantId && contextTenantId) {
          // Si toutes les sources pointent vers le même tenant, tout est synchronisé
          if (storedTenantId === currentTenantId && currentTenantId === contextTenantId) {
            console.log(`✅ TenantContext - Navigation: tenant correctement synchronisé partout: ${storedTenantId}`);
            return;
          }
        }
        
        // Déterminer le tenant à restaurer, avec priorité au localStorage car c'est l'intention utilisateur
        let tenantToRestore = storedTenantId || currentTenantId || contextTenantId;
        
        // Si aucun tenant n'est trouvé nulle part mais qu'il y a des tenants disponibles
        if (!tenantToRestore && tenants.length > 0) {
          tenantToRestore = tenants[0].id;
          console.log(`⚠️ TenantContext - Navigation: aucun tenant actif, utilisation du premier tenant disponible: ${tenantToRestore}`);
        }
        
        if (tenantToRestore) {
          // Vérifier si le tenant est dans la liste des tenants disponibles
          const tenantExists = tenants.some(t => t.id === tenantToRestore);
          
          if (tenantExists) {
            // Déterminer s'il faut restaurer le tenant
            const needsRestore = tenantToRestore !== contextTenantId || tenantToRestore !== currentTenantId;
            
            if (needsRestore) {
              console.log(`🔄 TenantContext - Navigation: restauration du tenant ${tenantToRestore}`);
              
              // Mettre à jour directement le TenantManager d'abord
              tenantManager.setCurrentTenant(tenantToRestore);
              
              // Ensuite mettre à jour le contexte
              const tenant = tenants.find(t => t.id === tenantToRestore) || null;
              console.log(`🔄 TenantContext - Navigation: mise à jour directe du contexte avec: ${tenant?.name || 'inconnu'}`);
              setCurrentTenant(tenant);
              
              // Enfin, mettre à jour le localStorage
              localStorage.setItem('currentTenantId', tenantToRestore);
              
              // Appliquer le branding du tenant
              if (tenant) {
                console.log(`🎨 TenantContext - Navigation: application de la personnalisation pour: ${tenant.name}`);
                tenantBrandingService.applyBranding(tenantToRestore);
              }
              
              // Signaler le changement de tenant via un événement (utiliser un délai pour éviter les boucles)
              setTimeout(() => {
                const event = new CustomEvent('tenantDataChanged', { detail: { tenantId: tenantToRestore } });
                window.dispatchEvent(event);
                document.dispatchEvent(event);
              }, 300);
            }
          } else if (tenants.length > 0) {
            // Si le tenant à restaurer n'existe pas mais qu'il y a des tenants disponibles
            console.log(`⚠️ TenantContext - Navigation: le tenant ${tenantToRestore} n'existe pas, utilisation du premier tenant disponible`);
            localStorage.setItem('currentTenantId', tenants[0].id);
            tenantManager.setCurrentTenant(tenants[0].id);
            setCurrentTenant(tenants[0]);
            tenantBrandingService.applyBranding(tenants[0].id);
          }
        }
      } catch (error) {
        console.error('❌ TenantContext - Erreur lors de la restauration du tenant:', error);
      }
    };
    
    // Vérifier si la page actuelle nécessite un tenant (exclure certaines pages comme login, etc.)
    const isPageRequiringTenant = pathname ? (!pathname.includes('/login') && !pathname.includes('/register')) : true;
    
    // Restaurer le tenant lors des changements de page, mais seulement si nécessaire
    if (isPageRequiringTenant) {
      // Utiliser un délai court pour éviter les conflits avec d'autres effets
      const timer = setTimeout(() => {
        restoreTenantFromStorage().catch(err => {
          console.error('❌ TenantContext - Erreur lors de la restauration du tenant:', err);
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
    return () => {}; // Retourner une fonction de nettoyage vide si la page ne nécessite pas de tenant
  }, [pathname, tenants, currentTenant]); // Dépendre du pathname, des tenants et du tenant actuel

  // Fonction pour obtenir un tenant par son ID
  const getTenantById = (tenantId: string): Tenant | undefined => {
    return tenants.find(t => t.id === tenantId);
  };


  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        tenants,
        isLoading,
        switchTenant,
        getTenantById,
        reloadTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;